import React from 'react';
import { Box, Button, DropdownMenu, Flex, Icon, Menu, MenuDivider, MenuItem, Text, Tooltip } from 'core/components';
import styled from 'styled-components';
import { getMeasurementItem } from '@kentik/ui-shared/query/measurements';
import { Measurement, MeasurementMetric, RollupFunction } from '@kentik/ui-shared/query/query.types';
import { ENUM_ROLLUP_OPTIONS, getDimensionDisplayLabel, ROLLUP_OPTIONS } from '@kentik/ui-shared/query/query';
import { isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import { themeGet } from 'styled-system';
import {
  getAnimationStyle,
  getEnterAnimation,
  getExitAnimation
} from 'app/views/boards/panels/ude/selectionAnimations';
import { InputGroup, PopoverPosition } from '@blueprintjs/core';

const ItemWrapper = styled(Flex)`
  .drag-handle {
    opacity: 0.35;
  }

  li {
    list-style: none;
    flex: 1;
  }

  &:hover {
    .drag-handle {
      opacity: 1;
    }
  }
`;

const AnimatedWrapper = styled.div<{ $isEntering: boolean; $isExiting: boolean }>`
  ${(props) => {
    const style = getAnimationStyle();
    if (props.$isExiting) {
      return getExitAnimation(style);
    }
    if (props.$isEntering) {
      return getEnterAnimation(style);
    }
    return '';
  }}
`;

export const WrappedMenuitem = styled(MenuItem)`
  cursor: unset;
  flex: 1;
  width: 100%;

  &.bp4-menu-item {
    display: flex;
    flex: 1;
    align-items: center;
    padding-left: 4px;
    padding-right: 4px;
    height: 28px;
    font-size: 12px;
    border-radius: 16px;
    line-height: unset;
    background-color: ${({ theme }) => (theme.name === 'light' ? '#f0f1f4' : '#252525')};
    color: ${themeGet('colors.body')};
  }
`;

const DragHandle = SortableHandle(() => (
  <Icon className="drag-handle" icon="drag-handle-vertical" iconSize={14} style={{ cursor: 'grab' }} />
));

interface MetricOrDimensionItemProps {
  measurement: string;
  value: string;
  type: 'metric' | 'dimension';
  index?: number;
  onRemove?: () => void;
  reorderable?: boolean;
  rollupFns?: RollupFunction[];
  onRollupChange?: (fns: RollupFunction[]) => void;
  customPercentiles?: RollupFunction[];
  measurements?: Measurement[];
  isEntering?: boolean;
  isExiting?: boolean;
}

export const MetricOrDimensionItemContents = (props: MetricOrDimensionItemProps) => {
  const {
    measurement,
    value,
    type,
    onRemove = null,
    reorderable,
    index,
    rollupFns = ['avg'],
    onRollupChange,
    customPercentiles = [],
    measurements,
    isEntering = false,
    isExiting = false
  } = props;
  const [showCustomInput, setShowCustomInput] = React.useState(false);
  const [customValue, setCustomValue] = React.useState('');
  const item = getMeasurementItem(measurement, type, value, measurements);

  const handleAddCustomPercentile = (num: number) => {
    if (num >= 1 && num <= 99 && onRollupChange) {
      const fn = `p${num}` as RollupFunction;
      if (!rollupFns.includes(fn)) {
        onRollupChange([...rollupFns, fn]);
      }
      setCustomValue('');
      setShowCustomInput(false);
    }
  };

  const handleCustomPercentileKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const num = parseInt(customValue, 10);
      handleAddCustomPercentile(num);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setCustomValue('');
      setShowCustomInput(false);
    }
  };

  const handleCustomPercentileClick = () => {
    const num = parseInt(customValue, 10);
    handleAddCustomPercentile(num);
  };

  if (!item) {
    // Render a minimal fallback so SortableElement's findDOMNode never returns null
    console.error(`Invalid ${type}: ${measurement}/${type}/${value}`);
    return (
      <WrappedMenuitem
        text={`Invalid: ${value}`}
        label={
          onRemove ? (
            <Tooltip content="Remove" placement="top">
              <Button
                minimal
                small
                borderRadius="24"
                icon="small-cross"
                onClick={(e: { stopPropagation: () => void }) => {
                  e.stopPropagation();
                  onRemove();
                }}
                aria-label="Remove"
              />
            </Tooltip>
          ) : undefined
        }
      />
    );
  }

  // For dimensions, add src/dst prefix to the display label when applicable
  const baseLabel = item.Label || value;
  const displayLabel = type === 'dimension' ? getDimensionDisplayLabel(value, baseLabel) : baseLabel;
  const isEnum = isEnumItem(item);
  const rollupOptions = isEnum ? ENUM_ROLLUP_OPTIONS : ROLLUP_OPTIONS;

  return (
    <AnimatedWrapper $isEntering={isEntering} $isExiting={isExiting}>
      <ItemWrapper alignItems="center" index={index}>
        <WrappedMenuitem
          text={
            <Flex alignItems="center" gap={1}>
              {reorderable && <DragHandle />}
              <Text fontWeight="medium">{displayLabel}</Text>
            </Flex>
          }
          label={
            <Flex alignItems="center">
              {type === 'metric' && (item as MeasurementMetric).Rollup !== false && (
                <DropdownMenu boundary="viewport" position={PopoverPosition.BOTTOM_RIGHT}>
                  <Tooltip
                    content="Select aggregations"
                    hoverCloseDelay={0}
                    openOnTargetFocus={false}
                    position="bottom"
                  >
                    <Button
                      small
                      minimal
                      color="muted"
                      text={rollupFns
                        .map((fn) => {
                          const opt = ROLLUP_OPTIONS.find((o) => o.value === fn);
                          return opt ? opt.label : fn;
                        })
                        .join(', ')}
                      rightIcon="caret-down"
                      style={{ minHeight: 'unset', height: 18 }}
                    />
                  </Tooltip>
                  <Box p="4px" overflow="auto">
                    <Box px="7px" py="4px">
                      <Text small muted>
                        Select aggregations for <strong>{displayLabel}</strong>
                      </Text>
                    </Box>
                    <Menu>
                      {rollupOptions.map((option) => {
                        const isSelected = rollupFns.includes(option.value);
                        return (
                          <MenuItem
                            key={option.value}
                            text={option.label}
                            iconColor={isSelected ? 'primary' : undefined}
                            icon={isSelected ? 'small-tick' : 'blank'}
                            active={isSelected}
                            shouldDismissPopover={false}
                            onClick={() => {
                              if (onRollupChange) {
                                if (isSelected && rollupFns.length > 1) {
                                  onRollupChange(rollupFns.filter((fn) => fn !== option.value));
                                } else if (!isSelected) {
                                  onRollupChange([...rollupFns, option.value]);
                                }
                              }
                            }}
                          />
                        );
                      })}
                      {customPercentiles.length > 0 && (
                        <>
                          {customPercentiles.map((cp) => {
                            const isSelected = rollupFns.includes(cp);
                            return (
                              <MenuItem
                                key={cp}
                                text={cp}
                                iconColor={isSelected ? 'primary' : undefined}
                                icon={isSelected ? 'small-tick' : 'blank'}
                                active={isSelected}
                                shouldDismissPopover={false}
                                onClick={() => {
                                  if (onRollupChange) {
                                    if (isSelected && rollupFns.length > 1) {
                                      onRollupChange(rollupFns.filter((fn) => fn !== cp));
                                    } else if (!isSelected) {
                                      onRollupChange([...rollupFns, cp]);
                                    }
                                  }
                                }}
                              />
                            );
                          })}
                        </>
                      )}
                      {!isEnum && (
                        <>
                          <MenuDivider />
                          {!showCustomInput ? (
                            <MenuItem
                              text="Custom percentile..."
                              icon="plus"
                              shouldDismissPopover={false}
                              onClick={() => setShowCustomInput(true)}
                            />
                          ) : (
                            <>
                              <MenuDivider title="Add custom percentile" />
                              <Flex alignItems="center" px="7px" py="2px" gap="4px">
                                <Text small>p</Text>
                                <InputGroup
                                  small
                                  fill
                                  autoFocus
                                  placeholder="1–99"
                                  value={customValue}
                                  style={{ width: 50 }}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomValue(e.target.value)}
                                  onKeyDown={handleCustomPercentileKeyDown}
                                />
                                <Button small text="Add" icon="small-plus" onClick={handleCustomPercentileClick} />
                              </Flex>
                            </>
                          )}
                        </>
                      )}
                    </Menu>
                  </Box>
                </DropdownMenu>
              )}

              <Button
                minimal
                small
                borderRadius="24px"
                icon="small-cross"
                onClick={(e: { stopPropagation: () => void }) => {
                  e.stopPropagation();
                  if (onRemove) {
                    onRemove();
                  }
                }}
                aria-label="Remove"
              />
            </Flex>
          }
        />
      </ItemWrapper>
    </AnimatedWrapper>
  );
};

export const MetricOrDimensionItem = SortableElement<MetricOrDimensionItemProps>(
  (props: MetricOrDimensionItemProps) => <MetricOrDimensionItemContents {...props} />
);
